import { Auth0Context, type Auth0ContextInterface, initialContext, type User } from "@auth0/auth0-react";
import { type ReactNode, useMemo } from "react";

import { ROLES_CLAIM } from "@/lib/permissions";
import { clearSandboxAgentToken } from "@/lib/sandbox-agent";

/** The admin the agent's token speaks for (claims set by the Auth0 Action, apps/sandbox/README.md « Agent login »). */
function userFromToken(token: string): User {
  const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
  const email = payload["https://upclaim.eu/claims/user"]?.email;
  return {
    sub: payload.sub,
    email,
    name: email,
    [ROLES_CLAIM]: payload["https://upclaim.eu/claims/roles"] ?? [],
  };
}

/** Stands in for Auth0Provider when an AI agent opens a local sandbox with its token (readSandboxAgentToken). */
export function SandboxAgentAuthProvider({ token, children }: { token: string; children: ReactNode }) {
  const value = useMemo<Auth0ContextInterface>(
    () => ({
      ...initialContext,
      isAuthenticated: true,
      isLoading: false,
      error: undefined,
      user: userFromToken(token),
      getAccessTokenSilently: (async () => token) as Auth0ContextInterface["getAccessTokenSilently"],
      loginWithRedirect: async () => {
        // The session is the agent's token: no login page to go to.
      },
      logout: async () => {
        clearSandboxAgentToken();
        window.location.assign("/");
      },
    }),
    [token],
  );

  return <Auth0Context.Provider value={value}>{children}</Auth0Context.Provider>;
}
